/**
 * **Releases** (`/releases`) — a folder per release, and nothing else.
 *
 * The command center's CRM ▸ Releases page (`crm/pages/releases/ReleasesPage`),
 * as a reseller sees it (2026-09-14): the same folder grid, the same count
 * chips, the same tile — and no "New release", no Edit, no Delete. A reseller
 * takes what XOP ships; it changes nothing here. Opening a folder NAVIGATES
 * to that release's own page (`/releases/10.2`, `ReleaseDetailPage`).
 *
 * It replaced the Software Downloads page, which listed every product,
 * release and version on one long scroll. Only SHIPPED releases are here —
 * `groupIntoFamilies` leaves XOP's roadmap out.
 *
 * Who may see which file is still decided upstream, in ONE place:
 * portal-api's `Releases::ArtifactAccess` decides AND records every download
 * attempt. Gated on `documentation:view` like Documentation — "may
 * read what XOP publishes".
 */
import { Box, Button, Card, CardContent, Chip, Typography } from "@mui/material";
import type { JSX } from "react";
import { useNavigate } from "react-router-dom";

import { PageHeader } from "../../../components/ui/page-header.js";
import { PageLoader } from "../../../components/ui/page-loader.js";
import { Pagination } from "../../../components/ui/pagination.js";
import { usePagedList } from "../../../hooks/usePagedList.js";
import { DownloadRefused } from "./api.js";
import { ReleaseFolderCard } from "./ReleaseFolderCard.js";
import { releasePath } from "./release-paths.js";
import { statusLabel } from "./release-status.js";
import { useReleases } from "./useReleases.js";

export const RELEASES_TITLE = "Software Releases";

export function ReleasesPage(): JSX.Element {
  const navigate = useNavigate();
  const { families, latest, versionCount, isLoading, isError, error, refetch } = useReleases();
  const { paged, page, perPage, totalCount, totalPages, setPage, setPerPage } =
    usePagedList(families, 25);

  return (
    <Box>
      <PageHeader title={RELEASES_TITLE} />

      <Card sx={{ mt: 3 }}>
        {families.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
              borderBottom: 1,
              borderColor: "divider",
              px: 2.5,
              py: 1.5,
            }}
          >
            <Chip
              size="small"
              variant="outlined"
              label={`${families.length} release${families.length === 1 ? "" : "s"} · ${versionCount} version${versionCount === 1 ? "" : "s"}`}
              sx={{ height: 22 }}
            />
            {latest && (
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={`Latest ${latest.version} · ${statusLabel(latest.status)}`}
                sx={{ height: 22 }}
              />
            )}
          </Box>
        )}
        <CardContent sx={{ px: 2.5, py: 2 }}>
          {isLoading ? (
            <PageLoader />
          ) : isError ? (
            <Box sx={{ borderRadius: 1, border: 1, borderColor: "error.main", p: 2, fontSize: 14 }}>
              <Typography sx={{ mb: 1, color: "error.main" }}>
                Could not load releases
                {error instanceof DownloadRefused && error.message ? `: ${error.message}` : "."}
              </Typography>
              <Button variant="contained" size="small" onClick={() => void refetch()}>
                Retry
              </Button>
            </Box>
          ) : families.length === 0 ? (
            <Typography sx={{ py: 3, textAlign: "center", fontSize: 14, color: "text.disabled" }}>
              No released versions yet.
            </Typography>
          ) : (
            <>
              <Box
                data-testid="release-folders"
                sx={{
                  display: "grid",
                  gap: 1.5,
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    lg: "repeat(3, minmax(0, 1fr))",
                  },
                }}
              >
                {paged.map((family) => (
                  <ReleaseFolderCard
                    key={family.line.id}
                    family={family}
                    onOpen={() => navigate(releasePath(family.line.version))}
                  />
                ))}
              </Box>
              <Box sx={{ mt: 1.5 }}>
                <Pagination
                  page={page}
                  perPage={perPage}
                  totalCount={totalCount}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  onPerPageChange={setPerPage}
                  entityNoun="releases"
                  hideIfSinglePage
                />
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default ReleasesPage;
